import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Alert, Tabs, Tab } from 'react-bootstrap';
import { getEmployeeLeaveApplications, updateLeaveApplication, mapLeaveStatusToString } from '../services/leaveService';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const LeaveApplicationScreen = () => {
  console.log('=== LeaveApplicationScreen Component Render ===');
  
  const [leaveApplications, setLeaveApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    leaveType: 'Short Leave',
    leaveFrom: '',
    leaveTo: '',
    reason: '',
    remark: ''
  });

  useEffect(() => {
    console.log('=== useEffect Hook Start ===');
    const fetchData = async () => {
      try {
        console.log('Setting loading state to true');
        setLoading(true);
        setError(null);
        
        // Get employee details from localStorage
        const employeeCode = localStorage.getItem('employeeCode');
        const companyCode = localStorage.getItem('companyCode');
        
        console.log('LocalStorage values:', {
          employeeCode: employeeCode ? 'Present' : 'Missing',
          companyCode: companyCode ? 'Present' : 'Missing'
        });
        
        if (!employeeCode || !companyCode) {
          console.error('Missing required employee details');
          throw new Error('Employee details not found. Please login again.');
        }
        
        console.log('Fetching leave applications with params:', {
          employeeCode,
          companyCode
        });
        
        const data = await getEmployeeLeaveApplications(employeeCode, companyCode);
        console.log('Received leave applications data:', data);
        
        if (Array.isArray(data)) {
          console.log('Data is array, sorting applications');
          // Sort applications by date (newest first)
          const sortedApplications = data.sort((a, b) => 
            new Date(b.AppDate) - new Date(a.AppDate)
          ).map(leave => ({
            ...leave,
            LeaveStatus: mapLeaveStatusToString(leave.LeaveStatus)
          }));
          console.log('Sorted applications:', sortedApplications);
          setLeaveApplications(sortedApplications);
          
          // If no applications found, show appropriate message
          if (sortedApplications.length === 0) {
            console.log('No leave applications found');
            setError('No leave applications found. You can create a new application using the button above.');
          }
        } else {
          console.error('Invalid data format received:', data);
          setError('Invalid data format received from server');
        }
      } catch (error) {
        console.error('Error in fetchData:', error);
        if (error.message.includes('login again') || 
            error.message.includes('Session expired')) {
          console.log('Authentication error detected, redirecting to login');
          localStorage.clear();
          window.location.href = '/login';
        } else {
          // For other errors, show a user-friendly message
          console.warn('Non-critical error loading leave applications:', error.message);
          setError('Unable to load existing leave applications at the moment. You can still submit new applications.');
        }
      } finally {
        console.log('Setting loading state to false');
        setLoading(false);
      }
    };

    fetchData();
    console.log('=== useEffect Hook End ===');
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log('Form input changed:', { name, value });
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('=== Leave Application Form Submission Start ===');
    try {
      setError(null);
      setLoading(true);

      // Validate dates
      const fromDate = new Date(formData.leaveFrom);
      const toDate = new Date(formData.leaveTo);
      
      console.log('Validating dates:', {
        fromDate: fromDate.toISOString(),
        toDate: toDate.toISOString()
      });
      
      if (fromDate > toDate) {
        console.error('Invalid date range');
        setError('Leave end date cannot be before start date');
        setLoading(false);
        return;
      }

      // Calculate leave days
      const calculateLeaveDays = (fromDate, toDate) => {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        const diffTime = Math.abs(to - from);
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both dates
      };

      const leaveDays = calculateLeaveDays(formData.leaveFrom, formData.leaveTo);

      // Get and validate employee details
      const employeeDetails = {
        employeeCode: localStorage.getItem('employeeCode'),
        companyCode: localStorage.getItem('companyCode'),
        empAutoId: localStorage.getItem('empAutoId'),
        designation: localStorage.getItem('designation'),
        employeeType: localStorage.getItem('employeeType'),
        token: localStorage.getItem('token'),
        sessionToken: localStorage.getItem('sessionToken')
      };

      console.log('Employee details:', {
        ...employeeDetails,
        token: employeeDetails.token ? 'Present' : 'Missing',
        sessionToken: employeeDetails.sessionToken ? 'Present' : 'Missing'
      });

      // Validate all required fields
      const missingFields = Object.entries(employeeDetails)
        .filter(([key, value]) => !value)
        .map(([key]) => key);

      if (missingFields.length > 0) {
        throw new Error(`Missing required details: ${missingFields.join(', ')}. Please login again.`);
      }

      console.log('Form data:', formData);
      console.log('Calculated leave days:', leaveDays);
      
      const response = await updateLeaveApplication({
        ...formData,
        ...employeeDetails,
        days: leaveDays,
        applicationDate: new Date().toISOString(),
        status: 'Pending'
      });

      console.log('Leave application response:', response);

      if (response.success) {
        console.log('=== Leave Application Submitted Successfully ===');
        toast.success('Leave application submitted successfully');
        setShowForm(false);
        setFormData({
          leaveType: 'Short Leave',
          leaveFrom: '',
          leaveTo: '',
          reason: '',
          remark: ''
        });
        
        // Refresh the leave applications list
        console.log('Refreshing leave applications list...');
        const data = await getEmployeeLeaveApplications(
          employeeDetails.employeeCode, 
          employeeDetails.companyCode
        );
        
        if (Array.isArray(data)) {
          const sortedApplications = data.sort((a, b) => 
            new Date(b.AppDate) - new Date(a.AppDate)
          ).map(leave => ({
            ...leave,
            LeaveStatus: mapLeaveStatusToString(leave.LeaveStatus)
          }));
          setLeaveApplications(sortedApplications);
          console.log('Leave applications list refreshed');
        }
      } else {
        console.error('=== Leave Application Submission Failed ===', response.message);
        setError(response.message || 'Failed to submit leave application');
        toast.error(response.message || 'Failed to submit leave application');
      }
    } catch (error) {
      console.error('=== Leave Application Submission Error ===', error);
      if (error.message.includes('login again') || 
          error.message.includes('session expired') ||
          error.message.includes('authentication')) {
        console.log('Authentication error detected, redirecting to login...');
        localStorage.clear();
        window.location.href = '/login';
      }
      setError(error.message || 'Failed to submit leave application');
      toast.error(error.message || 'Failed to submit leave application');
    } finally {
      setLoading(false);
      console.log('=== Leave Application Form Submission End ===');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      return format(new Date(dateStr), 'dd-MM-yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateStr;
    }
  };

  const LeaveListTab = ({ status }) => {
    console.log(`Rendering LeaveListTab for status: ${status}`);
    const filteredLeaves = leaveApplications.filter(leave => leave.LeaveStatus === status);
    console.log(`Filtered leaves for ${status}:`, filteredLeaves);
    
    return (
      <div className="mt-3">
        {filteredLeaves.length === 0 ? (
          <Alert variant="info" className="text-center">
            {status === 'Pending' ? (
              <>
                <p>No pending leave applications found.</p>
                <p>Click the "New Leave Application" button above to apply for leave.</p>
              </>
            ) : (
              <>
                <p>No {status.toLowerCase()} leave applications found.</p>
                {status === 'Accepted' && (
                  <p>Your accepted leave applications will appear here.</p>
                )}
                {status === 'Rejected' && (
                  <p>Your rejected leave applications will appear here.</p>
                )}
                {status === 'Hold' && (
                  <p>Your leave applications on hold will appear here.</p>
                )}
              </>
            )}
          </Alert>
        ) : (
          filteredLeaves.map((leave) => (
            <Card key={leave.LVAPPID} className="mb-3" style={{ backgroundColor: '#e6f3ff' }}>
              <Card.Body>
                <h5 className="text-dark fw-bold">{leave.LVReason || "No Title"}</h5>
                <Row className="mt-3">
                  <Col md={6}>
                    <p><strong>Leave Type:</strong> {leave.LeavesType || 'N/A'}</p>
                    <p><strong>Leave Days:</strong> {leave.LVDays || 'N/A'}</p>
                    <p><strong>Applied On:</strong> {formatDate(leave.AppDate)}</p>
                  </Col>
                  <Col md={6}>
                    <p><strong>From:</strong> {formatDate(leave.LVDateF)}</p>
                    <p><strong>To:</strong> {formatDate(leave.LVDateT)}</p>
                    {leave.LVAppRejReason && (
                      <p><strong>Review Comments:</strong> {leave.LVAppRejReason}</p>
                    )}
                  </Col>
                </Row>
                <Row className="mt-3">
                  <Col md={6}>
                    {status === 'Pending' ? (
                      <Button 
                        variant="danger" 
                        className="w-100"
                        onClick={() => setShowForm(true)}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Button 
                        variant={status === 'Accepted' ? 'success' : status === 'Rejected' ? 'danger' : 'warning'}
                        className="w-100"
                        disabled
                      >
                        {status}
                      </Button>
                    )}
                  </Col>
                  <Col md={6}>
                    <Button 
                      variant="primary" 
                      className="w-100"
                      onClick={() => {/* TODO: Implement view details */}}
                    >
                      View Details
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          ))
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <Alert variant="info">Loading leave applications...</Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2>Leave Applications</h2>
        </Col>
        <Col xs="auto">
          <Button 
            variant="primary" 
            onClick={() => setShowForm(!showForm)}
            disabled={loading}
          >
            {showForm ? 'Cancel' : 'New Leave Application'}
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {showForm && (
        <Card className="mb-4">
          <Card.Body>
            <h4>New Leave Application</h4>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Leave Type</Form.Label>
                    <Form.Select
                      name="leaveType"
                      value={formData.leaveType}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                    >
                      <option value="Short Leave">Short Leave</option>
                      <option value="Half Leave">Half Leave</option>
                      <option value="Full Leave">Full Leave</option>
                      <option value="Sick Leave">Sick Leave</option>
                      <option value="Casual Leave">Casual Leave</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Leave From</Form.Label>
                    <Form.Control
                      type="date"
                      name="leaveFrom"
                      value={formData.leaveFrom}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Leave To</Form.Label>
                    <Form.Control
                      type="date"
                      name="leaveTo"
                      value={formData.leaveTo}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      min={formData.leaveFrom || new Date().toISOString().split('T')[0]}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Reason</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="reason"
                      value={formData.reason}
                      onChange={handleInputChange}
                      required
                      disabled={loading}
                      placeholder="Enter reason for leave"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Remarks</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="remark"
                      value={formData.remark}
                      onChange={handleInputChange}
                      disabled={loading}
                      placeholder="Enter any additional remarks (optional)"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? 'Submitting...' : 'Submit Application'}
              </Button>
            </Form>
          </Card.Body>
        </Card>
      )}

      <Tabs defaultActiveKey="pending" className="mb-3">
        <Tab eventKey="pending" title="Pending">
          <LeaveListTab status="Pending" />
        </Tab>
        <Tab eventKey="accepted" title="Accepted">
          <LeaveListTab status="Accepted" />
        </Tab>
        <Tab eventKey="rejected" title="Rejected">
          <LeaveListTab status="Rejected" />
        </Tab>
        <Tab eventKey="hold" title="Hold">
          <LeaveListTab status="Hold" />
        </Tab>
      </Tabs>
    </Container>
  );
};

export default LeaveApplicationScreen;